// ABOUTME: Drag and drop file upload component for resumes and documents
// ABOUTME: Provides visual feedback and validation for file uploads

import React, { useCallback, useState } from 'react';
import { Upload, File, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFileUpload } from '@/hooks/useFileUpload';

interface FileUploadProps {
  onFileUpload: (url: string, fileName: string) => void;
  acceptedTypes: string[];
  maxSizeInMB: number;
  currentFile?: string;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileUpload,
  acceptedTypes,
  maxSizeInMB,
  currentFile,
  disabled = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { uploadFile, isUploading } = useFileUpload();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await handleFileUpload(files[0]);
    }
  }, [disabled, isUploading]);

  const handleFileUpload = async (file: File) => {
    setSelectedFile(file);
    
    const url = await uploadFile(file, {
      bucket: 'resumes',
      allowedTypes: acceptedTypes,
      maxSizeInMB: maxSizeInMB,
    });

    if (url) {
      onFileUpload(url, file.name);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileUpload('', '');
  };

  const displayFileName = selectedFile?.name || (currentFile ? 'Uploaded file' : '');

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
          dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && !isUploading && document.getElementById('fileInput')?.click()}
      >
        <input
          id="fileInput"
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || isUploading}
        />
        
        {isUploading ? (
          <div className="space-y-2">
            <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : displayFileName ? (
          <div className="space-y-2">
            <File className="w-8 h-8 text-green-600 mx-auto" />
            <p className="text-sm font-medium text-gray-900">{displayFileName}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="mt-2"
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 text-gray-400 mx-auto" />
            <p className="text-sm text-gray-600">
              Drag and drop or <span className="text-blue-600 font-medium">browse files</span>
            </p>
            <p className="text-xs text-gray-500">
              PDF, DOC, DOCX up to 10MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
